export type DerogatoryLevel = "none" | "minor" | "major_seasoned" | "major_recent";

export interface ReadinessInputs {
  creditScore: number;
  monthlyIncome: number;
  monthlyDebt: number;
  downPaymentPercent: number;
  reserveMonths: number;
  employmentYears: number;
  derogatory: DerogatoryLevel;
}

export interface FactorScore {
  id: string;
  label: string;
  weight: number;
  points: number;
  detail: string;
  tip: string;
}

export interface ReadinessResult {
  score: number;
  band: BandId;
  bandLabel: string;
  dti: number;
  factors: FactorScore[];
  capped: boolean;
}

export type BandId = "ready" | "near" | "not";

export const FACTORS = [
  {
    id: "credit",
    label: "Credit score",
    weight: 35,
    what: "The mortgage-specific FICO scores lenders pull (FICO 2, 4, and 5) — not the score in a free consumer app.",
    strong: "740 or higher unlocks the best conventional pricing. 620 is a common conventional floor; some FHA lenders go to 580.",
  },
  {
    id: "dti",
    label: "Debt-to-income ratio",
    weight: 25,
    what: "Total monthly debt payments — including the future mortgage payment — divided by gross monthly income.",
    strong: "Under 36% is comfortable. Many programs allow up to 43%, and some go higher with strong compensating factors.",
  },
  {
    id: "cash",
    label: "Down payment and reserves",
    weight: 20,
    what: "Cash you can put down plus the months of mortgage payments you would still have left in the bank afterward.",
    strong: "20% down avoids mortgage insurance, but 3% to 5% is workable. Two to six months of reserves is a meaningful strength.",
  },
  {
    id: "history",
    label: "Financial history",
    weight: 10,
    what: "Late payments, collections, charge-offs, bankruptcies, and foreclosures, and how long ago they happened.",
    strong: "No derogatory marks in the last 24 months. Major events have program-specific waiting periods.",
  },
  {
    id: "employment",
    label: "Employment stability",
    weight: 10,
    what: "Length and consistency of income, including how self-employment or variable pay is documented.",
    strong: "Two years in the same job or field. Self-employed borrowers usually need two years of tax returns.",
  },
] as const;

export const BANDS: { id: BandId; label: string; min: number; max: number; meaning: string }[] = [
  {
    id: "ready",
    label: "Ready",
    min: 80,
    max: 100,
    meaning:
      "Your profile lines up with what most lenders look for. The productive next step is a real pre-approval with documents, not more preparation.",
  },
  {
    id: "near",
    label: "Near-Ready",
    min: 60,
    max: 79,
    meaning:
      "One or two factors are holding the profile back. These are usually fixable in a few months — a score bump, a paid-off card, or a few thousand more in reserves.",
  },
  {
    id: "not",
    label: "Not Ready",
    min: 0,
    max: 59,
    meaning:
      "Multiple factors need work, or there is a recent major credit event. A 6 to 24 month plan is realistic; a HUD-approved housing counselor can help you sequence it.",
  },
];

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

/** Piecewise-linear lookup across ascending breakpoints. */
const interp = (x: number, points: [number, number][]) => {
  if (x <= points[0][0]) return points[0][1];
  for (let i = 1; i < points.length; i++) {
    const [x0, y0] = points[i - 1];
    const [x1, y1] = points[i];
    if (x <= x1) return y0 + ((x - x0) / (x1 - x0)) * (y1 - y0);
  }
  return points[points.length - 1][1];
};

export const DEROGATORY_OPTIONS: { value: DerogatoryLevel; label: string }[] = [
  { value: "none", label: "None in the last 2 years" },
  { value: "minor", label: "A late payment or collection" },
  { value: "major_seasoned", label: "Bankruptcy or foreclosure, 4+ years ago" },
  { value: "major_recent", label: "Bankruptcy or foreclosure, within 3 years" },
];

export function calculateReadiness(input: ReadinessInputs): ReadinessResult {
  const income = Math.max(0, input.monthlyIncome);
  const dti = income > 0 ? clamp((input.monthlyDebt / income) * 100, 0, 200) : 100;

  const creditPts = clamp(
    interp(input.creditScore, [
      [500, 0],
      [580, 8],
      [620, 15],
      [660, 22],
      [700, 28],
      [740, 33],
      [780, 35],
    ]),
    0,
    35
  );

  const dtiPts = clamp(
    interp(dti, [
      [0, 25],
      [28, 25],
      [36, 21],
      [43, 15],
      [50, 7],
      [60, 0],
    ]),
    0,
    25
  );

  const dpPts = clamp(
    interp(input.downPaymentPercent, [
      [0, 0],
      [3, 4],
      [5, 6],
      [10, 9],
      [20, 12],
    ]),
    0,
    12
  );
  const reservePts = clamp(
    interp(input.reserveMonths, [
      [0, 0],
      [1, 3],
      [3, 6],
      [6, 8],
    ]),
    0,
    8
  );
  const cashPts = dpPts + reservePts;

  const historyPts =
    input.derogatory === "none"
      ? 10
      : input.derogatory === "minor"
        ? 6
        : input.derogatory === "major_seasoned"
          ? 5
          : 1;

  const employmentPts = clamp(
    interp(input.employmentYears, [
      [0, 2],
      [1, 6],
      [2, 10],
    ]),
    0,
    10
  );

  let score = Math.round(creditPts + dtiPts + cashPts + historyPts + employmentPts);
  let capped = false;
  // Guardrail: a very recent major credit event caps the result regardless of other strengths.
  if (input.derogatory === "major_recent" && score > 64) {
    score = 64;
    capped = true;
  }

  const factors: FactorScore[] = [
    {
      id: "credit",
      label: "Credit score",
      weight: 35,
      points: Math.round(creditPts),
      detail: `${Math.round(input.creditScore)} score`,
      tip:
        creditPts >= 33
          ? "Strong. Avoid opening new accounts or letting balances spike before closing."
          : "Pay revolving balances below 10% of each limit before the statement closes — that is usually the fastest legitimate lift.",
    },
    {
      id: "dti",
      label: "Debt-to-income ratio",
      weight: 25,
      points: Math.round(dtiPts),
      detail: `${dti.toFixed(0)}% of gross income`,
      tip:
        dtiPts >= 21
          ? "Comfortable. Keep new car loans and financed purchases off the table until after closing."
          : "Paying off the smallest installment loan removes its full payment from the ratio, which moves DTI faster than shaving balances.",
    },
    {
      id: "cash",
      label: "Down payment and reserves",
      weight: 20,
      points: Math.round(cashPts),
      detail: `${input.downPaymentPercent}% down, ${input.reserveMonths} mo. reserves`,
      tip:
        cashPts >= 15
          ? "Solid cash position. Season the funds for 60 days so they are easy to document."
          : "Check down payment assistance in your area before assuming you need more savings — many programs stack with 3% to 5% down loans.",
    },
    {
      id: "history",
      label: "Financial history",
      weight: 10,
      points: historyPts,
      detail: DEROGATORY_OPTIONS.find((o) => o.value === input.derogatory)?.label ?? "",
      tip:
        historyPts >= 10
          ? "Clean. Keep every account current through underwriting."
          : "Waiting periods after major events are program-specific and time is the main cure. Set up autopay on everything in the meantime.",
    },
    {
      id: "employment",
      label: "Employment stability",
      weight: 10,
      points: Math.round(employmentPts),
      detail: `${input.employmentYears} year${input.employmentYears === 1 ? "" : "s"}`,
      tip:
        employmentPts >= 10
          ? "Documented and stable. Avoid switching to 1099 or commission-only pay before closing."
          : "Staying in the same job or field until closing is worth more here than a raise at a new employer.",
    },
  ];

  const band = BANDS.find((b) => score >= b.min && score <= b.max) ?? BANDS[2];

  return { score, band: band.id, bandLabel: band.label, dti, factors, capped };
}

export const DEFAULT_INPUTS: ReadinessInputs = {
  creditScore: 700,
  monthlyIncome: 6500,
  monthlyDebt: 1800,
  downPaymentPercent: 5,
  reserveMonths: 2,
  employmentYears: 3,
  derogatory: "none",
};

export interface WorkedExample {
  id: string;
  name: string;
  bandLabel: string;
  inputs: ReadinessInputs;
  lift: string;
}

export const EXAMPLES: WorkedExample[] = [
  {
    id: "ready",
    name: "Maya — Ready",
    bandLabel: "Ready",
    inputs: {
      creditScore: 758,
      monthlyIncome: 8200,
      monthlyDebt: 2050,
      downPaymentPercent: 10,
      reserveMonths: 5,
      employmentYears: 4,
      derogatory: "none",
    },
    lift: "Already in range. The only lever left is pricing: getting from 758 to 760 crosses a common rate tier on conventional loans.",
  },
  {
    id: "near",
    name: "Devin — Near-Ready",
    bandLabel: "Near-Ready",
    inputs: {
      creditScore: 672,
      monthlyIncome: 5800,
      monthlyDebt: 2320,
      downPaymentPercent: 3,
      reserveMonths: 1,
      employmentYears: 3,
      derogatory: "minor",
    },
    lift: "Paying off the $480/month car loan drops DTI from 40% to 32% and moves this profile into Ready territory on its own.",
  },
  {
    id: "not",
    name: "Priya — Not Ready",
    bandLabel: "Not Ready",
    inputs: {
      creditScore: 596,
      monthlyIncome: 4200,
      monthlyDebt: 1930,
      downPaymentPercent: 0,
      reserveMonths: 0,
      employmentYears: 1,
      derogatory: "major_recent",
    },
    lift: "The recent bankruptcy caps the score no matter what else improves. Time plus rebuilding credit to 620 is the path — a secured card and perfect payment history for 12 months is the usual first move.",
  },
];

export const FAQS = [
  {
    id: "what-is-it",
    q: "What is a homebuyer readiness score?",
    a: "It is an educational summary metric that combines credit, debt-to-income, available cash, financial history, and employment stability into a single number so you can see where you stand before applying. It is not a credit score, and no lender underwrites off of it.",
  },
  {
    id: "who-uses-it",
    q: "Who uses a homebuyer readiness score?",
    a: "Housing counselors, homebuyer education programs, real estate and lending platforms, and employer homeownership benefits use readiness scoring to triage who is close to purchase-ready and who needs a preparation plan first. Buyers use it to prioritize which factor to work on next.",
  },
  {
    id: "standardized",
    q: "Is the formula standardized across tools?",
    a: "No. There is no industry-standard readiness formula, and the models behind most branded readiness scores are proprietary and not published. Different tools weight the same five factors differently, so scores are not comparable between products. The weighting shown on this page is illustrative and published openly so you can see exactly how the number moves.",
  },
  {
    id: "improve",
    q: "How do I improve my readiness score?",
    a: "Work the heaviest factor you can actually change. Credit carries the most weight, and lowering revolving utilization before the statement closing date is usually the fastest lift. After that, retiring a single installment loan removes its whole payment from your DTI, and adding reserves improves the cash factor even when the down payment percentage stays flat.",
  },
  {
    id: "vs-preapproval",
    q: "Is a readiness score the same as a pre-approval?",
    a: "No. A pre-approval is a lender's conditional decision based on verified documents and a hard credit pull. A readiness score is a self-reported estimate with no verification and no lending decision attached.",
  },
  {
    id: "credit-impact",
    q: "Does calculating a readiness score affect my credit?",
    a: "No. The calculator on this page runs entirely in your browser from numbers you type in. Nothing is pulled from a credit bureau and there is no inquiry of any kind.",
  },
];
